import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_API_URL || '') + '/api/public/book';

const inp = {
  width: '100%', padding: '10px 14px', border: '1.5px solid #d1e3ef',
  borderRadius: 8, fontSize: 15, outline: 'none', boxSizing: 'border-box',
  background: '#fff', color: '#1a1a2e',
};
const btn = (disabled) => ({
  background: disabled ? '#b0c9da' : '#0077b6', color: '#fff',
  border: 'none', borderRadius: 8, padding: '12px 28px',
  fontSize: 15, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  width: '100%', marginTop: 8,
});
const label = { display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 4 };
const errStyle = { color: '#c0392b', background: '#fdf0f0', border: '1px solid #e8c4c4', borderRadius: 6, padding: '10px 14px', fontSize: 14 };
const successStyle = { color: '#1a6b3c', background: '#f0fdf4', border: '1px solid #b2dfb9', borderRadius: 6, padding: '14px', fontSize: 14, textAlign: 'center' };

export default function PublicBookingPage() {
  const slug = window.location.pathname.split('/book/')[1]?.replace(/\/$/, '') || '';

  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    patientName: '', phone: '', preferredDate: '', preferredTime: '',
    treatment: '', notes: '', doctorId: '',
  });

  useEffect(() => {
    if (!slug) { setError('Invalid booking link.'); setLoading(false); return; }
    axios.get(`${API}/${slug}`)
      .then(r => { setClinic(r.data); setLoading(false); })
      .catch(() => { setError('Clinic not found or booking is unavailable.'); setLoading(false); });
  }, [slug]);

  useEffect(() => {
    if (!form.preferredDate || !slug) return;
    setSlotsLoading(true);
    const params = { date: form.preferredDate };
    if (form.doctorId) params.doctorId = form.doctorId;
    axios.get(`${API}/${slug}/slots`, { params })
      .then(r => { setSlots(r.data.slots || []); setSlotsLoading(false); })
      .catch(() => { setSlots([]); setSlotsLoading(false); });
  }, [form.preferredDate, form.doctorId, slug]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.patientName || !form.phone || !form.preferredDate) {
      setError('Please fill in your name, phone number, and preferred date.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const r = await axios.post(`${API}/${slug}`, form);
      setSubmitted(r.data);
    } catch (ex) {
      setError(ex.response?.data?.error || 'Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f7fc' }}>
      <p style={{ color: '#666' }}>Loading clinic information…</p>
    </div>
  );

  if (error && !clinic) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f7fc' }}>
      <div style={{ ...errStyle, maxWidth: 420, textAlign: 'center' }}>{error}</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f0f7fc', padding: '24px 16px', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 36 }}>🦷</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#003554', margin: '8px 0 4px' }}>{clinic?.clinicName}</h1>
          <p style={{ color: '#666', fontSize: 14, margin: 0 }}>Book an appointment online</p>
        </div>

        {submitted ? (
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, boxShadow: '0 2px 16px #0002' }}>
            <div style={successStyle}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
              <strong>Booking Request Submitted!</strong>
              <p style={{ marginTop: 8, color: '#2d7d4e' }}>{submitted.message}</p>
              <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Booking ID: {submitted.bookingId}</p>
            </div>
            <button onClick={() => { setSubmitted(null); setForm({ patientName: '', phone: '', preferredDate: '', preferredTime: '', treatment: '', notes: '', doctorId: '' }); }}
              style={{ ...btn(false), marginTop: 16, background: '#0077b6' }}>
              Book Another Appointment
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ background: '#fff', borderRadius: 14, padding: 28, boxShadow: '0 2px 16px #0002' }}>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: '#003554', marginTop: 0, marginBottom: 20 }}>Your Details</h2>

            {error && <div style={{ ...errStyle, marginBottom: 16 }}>{error}</div>}

            <div style={{ marginBottom: 16 }}>
              <label style={label}>Full Name <span style={{ color: 'red' }}>*</span></label>
              <input style={inp} placeholder="Enter your full name" value={form.patientName}
                onChange={e => set('patientName', e.target.value)} required />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={label}>Phone Number <span style={{ color: 'red' }}>*</span></label>
              <input style={inp} placeholder="10-digit mobile number" value={form.phone} type="tel"
                onChange={e => set('phone', e.target.value)} required />
            </div>

            {clinic?.doctors?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={label}>Preferred Doctor (optional)</label>

                {/* J1 — Doctor cards with trust badges (success rate + satisfaction) */}
                <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
                  {clinic.doctors.map(d => {
                    const selected = form.doctorId === d._id;
                    return (
                      <div key={d._id} onClick={() => set('doctorId', selected ? '' : d._id)}
                        style={{
                          padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                          border: selected ? '2px solid #00B5B8' : '1px solid #e5e7eb',
                          background: selected ? '#ecfeff' : '#fff',
                          transition: '0.15s',
                        }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                          <div>
                            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>{d.name}</div>
                            <div style={{ fontSize: '0.78rem', color: '#666' }}>
                              {d.qualification ? `${d.qualification}` : ''}
                              {d.specialization ? ` · ${d.specialization}` : ''}
                            </div>
                          </div>
                          {d.stats && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, background: '#dcfce7', color: '#166534' }}>
                                ✓ {d.stats.successRate}% success
                              </span>
                              {d.stats.avgSatisfaction != null && (
                                <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, background: '#fef3c7', color: '#92400e' }}>
                                  ⭐ {d.stats.avgSatisfaction}/5
                                </span>
                              )}
                              <span style={{ fontSize: '0.7rem', color: '#666', alignSelf: 'center' }}>
                                ({d.stats.total} cases)
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                  Click a doctor to select, or leave unselected for <i>Any available</i>.
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={label}>Preferred Date <span style={{ color: 'red' }}>*</span></label>
              <input style={inp} type="date" min={today} value={form.preferredDate}
                onChange={e => set('preferredDate', e.target.value)} required />
            </div>

            {form.preferredDate && (
              <div style={{ marginBottom: 16 }}>
                <label style={label}>Preferred Time</label>
                {slotsLoading ? (
                  <p style={{ color: '#888', fontSize: 13 }}>Loading available slots…</p>
                ) : slots.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {slots.filter(s => s.available).map(s => (
                      <button type="button" key={s.time}
                        onClick={() => set('preferredTime', s.time)}
                        style={{
                          padding: '6px 14px', borderRadius: 20, border: '1.5px solid',
                          borderColor: form.preferredTime === s.time ? '#0077b6' : '#d1e3ef',
                          background: form.preferredTime === s.time ? '#0077b6' : '#f5f9fc',
                          color: form.preferredTime === s.time ? '#fff' : '#333',
                          fontSize: 13, cursor: 'pointer',
                        }}>
                        {s.time}
                      </button>
                    ))}
                    {slots.filter(s => s.available).length === 0 && (
                      <p style={{ color: '#888', fontSize: 13 }}>No slots available on this date. Please choose another date.</p>
                    )}
                  </div>
                ) : (
                  <input style={inp} type="time" value={form.preferredTime}
                    onChange={e => set('preferredTime', e.target.value)} placeholder="HH:MM" />
                )}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={label}>Treatment / Reason for Visit</label>
              <input style={inp} placeholder="e.g. Cleaning, Root Canal, Checkup…" value={form.treatment}
                onChange={e => set('treatment', e.target.value)} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={label}>Additional Notes</label>
              <textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} placeholder="Any specific concerns or information for the doctor…"
                value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>

            <button type="submit" style={btn(submitting)} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Request Appointment'}
            </button>
            <p style={{ textAlign: 'center', fontSize: 12, color: '#aaa', marginTop: 12 }}>
              The clinic will confirm your appointment. You will receive a WhatsApp confirmation.
            </p>
          </form>
        )}

        <p style={{ textAlign: 'center', fontSize: 11, color: '#bbb', marginTop: 20 }}>
          Powered by <strong>ORALZY</strong>
        </p>
      </div>
    </div>
  );
}
